/**
 * SavedViewSelector — 保存/加载用户视图配置
 *
 * 下拉选择已保存视图 / "保存当前视图" / 设为默认 / 删除
 * 嵌入在 EntityListPage 搜索栏旁边
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Bookmark,
  BookmarkCheck,
  Star,
  Trash2,
  Save,
  X,
  ChevronDown,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

export interface SavedViewConfig {
  columns?: string[];
  sort?: string;
  order?: 'asc' | 'desc';
  filters?: Record<string, string>;
  pageSize?: number;
  statusFilter?: string;
  /** Active template override (Classic/Compact/etc.) saved with this view */
  templateId?: string;
}

interface SavedView {
  id: string;
  entitySlug: string;
  name: string;
  isDefault: boolean;
  config: SavedViewConfig;
  createdAt: string;
  updatedAt: string;
}

export interface SavedViewSelectorProps {
  entitySlug: string;
  /** Current view state to save */
  currentConfig: SavedViewConfig;
  /** Called when a saved view is selected */
  onApply: (config: SavedViewConfig) => void;
  /** Called when the active saved view is cleared */
  onReset?: () => void;
  /** Optional active view id from route/query state */
  selectedViewId?: string | null;
  /** Called when active view id changes */
  onViewSelected?: (viewId: string | null) => void;
}

// ─── Component ─────────────────────────────────────

export function SavedViewSelector({
  entitySlug,
  currentConfig,
  onApply,
  onReset,
  selectedViewId,
  onViewSelected,
}: SavedViewSelectorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [views, setViews] = useState<SavedView[]>([]);
  const [open, setOpen] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  // ─── Fetch views ─────────────────────────────
  const loadViews = useCallback(async () => {
    try {
      const res = await api.get<SavedView[]>(`/saved-views?entity=${entitySlug}`);
      setViews(res ?? []);
    } catch {
      setViews([]);
    }
  }, [entitySlug]);

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  useEffect(() => {
    if (!selectedViewId || selectedViewId === activeViewId) return;
    const selectedView = views.find((view) => view.id === selectedViewId);
    if (!selectedView) return;
    setActiveViewId(selectedView.id);
    onApply(selectedView.config);
  }, [activeViewId, onApply, selectedViewId, views]);

  // Auto-apply default view on first load
  useEffect(() => {
    if (selectedViewId) return;
    const defaultView = views.find((v) => v.isDefault);
    if (defaultView && !activeViewId) {
      setActiveViewId(defaultView.id);
      onApply(defaultView.config);
    }
  }, [activeViewId, onApply, selectedViewId, views]);

  // ─── Save current view ───────────────────────
  const handleSave = async () => {
    if (!saveName.trim()) return;
    try {
      await api.post('/saved-views', {
        entitySlug,
        name: saveName.trim(),
        config: currentConfig,
      });
      toast.success(t('savedViews.saved', { defaultValue: 'View saved' }));
      setSaveName('');
      setShowSave(false);
      void loadViews();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    }
  };

  // ─── Set default ─────────────────────────────
  const handleSetDefault = async (id: string) => {
    try {
      await api.post(`/saved-views/${id}/set-default`, {});
      toast.success(t('savedViews.setDefault', { defaultValue: 'Default view updated' }));
      void loadViews();
    } catch {
      toast.error('Failed to set default');
    }
  };

  // ─── Delete view ─────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/saved-views/${id}`);
      if (activeViewId === id) setActiveViewId(null);
      void loadViews();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ─── Select view ─────────────────────────────
  const handleSelect = (view: SavedView) => {
    setActiveViewId(view.id);
    onApply(view.config);
    onViewSelected?.(view.id);
    setOpen(false);
  };

  const activeView = views.find((v) => v.id === activeViewId);

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        variant="outline"
        size="sm"
        className="h-9 gap-1.5"
        onClick={() => setOpen(!open)}
      >
        {activeView ? (
          <BookmarkCheck className="h-3.5 w-3.5 text-primary" />
        ) : (
          <Bookmark className="h-3.5 w-3.5" />
        )}
        <span className="max-w-[120px] truncate text-xs">
          {activeView?.name ?? t('savedViews.views', { defaultValue: 'Views' })}
        </span>
        <ChevronDown className="h-3 w-3 opacity-50" />
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-10 z-50 w-64 rounded-md border bg-popover text-popover-foreground shadow-md">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-xs font-medium">
              {t('savedViews.title', { defaultValue: 'Saved Views' })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs gap-1"
              onClick={() => { setShowSave(true); setOpen(false); }}
            >
              <Save className="h-3 w-3" />
              {t('savedViews.saveBtn', { defaultValue: 'Save Current' })}
            </Button>
          </div>

          {/* Views List */}
          <div className="max-h-60 overflow-y-auto">
            {views.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground">
                {t('savedViews.empty', { defaultValue: 'No saved views' })}
              </div>
            ) : (
              views.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 ${
                    v.id === activeViewId ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleSelect(v)}
                >
                  <span className="flex-1 truncate text-sm">{v.name}</span>
                  {v.isDefault && <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />}
                  <button
                    className="opacity-0 group-hover:opacity-100 hover:text-amber-500 p-0.5"
                    title={t('savedViews.setDefault', { defaultValue: 'Set as default' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleSetDefault(v.id);
                    }}
                  >
                    <Star className="h-3 w-3" />
                  </button>
                  <button
                    className="opacity-0 group-hover:opacity-100 hover:text-destructive p-0.5"
                    title={t('savedViews.delete', { defaultValue: 'Delete' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(v.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Reset button */}
          {activeViewId && (
            <div className="border-t px-3 py-2">
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setActiveViewId(null);
                  setOpen(false);
                  onViewSelected?.(null);
                  onReset?.();
                }}
              >
                {t('savedViews.reset', { defaultValue: 'Reset to default columns' })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Save Dialog (inline) */}
      {showSave && (
        <div className="absolute left-0 top-10 z-50 w-64 rounded-md border bg-popover p-3 shadow-md">
          <p className="mb-2 text-xs font-medium">
            {t('savedViews.saveName', { defaultValue: 'View Name' })}
          </p>
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder={t('savedViews.namePlaceholder', { defaultValue: 'My custom view' })}
            className="mb-2 h-8 text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') setShowSave(false);
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setShowSave(false)}>
              <X className="mr-1 h-3 w-3" />
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button size="sm" className="h-7" onClick={() => void handleSave()} disabled={!saveName.trim()}>
              <Save className="mr-1 h-3 w-3" />
              {t('common.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
